import { makeAutoObservable, runInAction } from 'mobx'
import { RequestLoadData } from 'shared/api/lib'
import {
  type Integration,
  IntegrationsApi,
  type IIntegrationOrganizationSettingsResponse,
} from 'entities/Integrations'

type IAgentforcePackageType = 'production' | 'sandbox'

const AGENTFORCE_SELECT_ITEMS: { id: IAgentforcePackageType; label: string }[] = [
  { id: 'production', label: 'Agentforce Production' },
  { id: 'sandbox', label: 'Agentforce Sandbox' },
]

export class IntegrationAgentforceSettingsStore {
  private _packageType: IAgentforcePackageType = 'production'
  private _installationUrl = ''
  private _installationUrlSandbox = ''

  constructor(private _integration: Integration) {
    makeAutoObservable(this)
  }

  init = () => {
    this._requestLoad.loadData()
  }

  dispose = () => {
    this._requestLoad.dispose()
    this._requestUpdate.dispose()
  }

  private _requestLoad = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      const [version, settings] = await Promise.all([
        IntegrationsApi.getIntegrationVersion(this._integration.key, {
          cancelToken: cancelRequest.token,
        }),
        IntegrationsApi.getIntegrationOrganizationSetting({
          cancelToken: cancelRequest.token,
        }),
      ])
      runInAction(() => {
        this._installationUrl = version.data.installation_url
        this._installationUrlSandbox = version.data.installation_url_sandbox
        this._applySettingsResponse(settings.data)
      })
    },
  })

  private _applySettingsResponse = (response: IIntegrationOrganizationSettingsResponse) => {
    const item = response.data.find((i) => i.key === 'agentforce_package_type')
    if (item) {
      this._packageType = item.value
    }
  }

  private _requestUpdate = new RequestLoadData<IAgentforcePackageType>({
    requestCallback: async (_config, value) => {
      if (!value) return
      await IntegrationsApi.putIntegrationOrganizationSetting({
        key: 'agentforce_package_type',
        value,
      })
    },
  })

  setPackageType = (value: IAgentforcePackageType) => {
    this._packageType = value
    this._requestUpdate.loadData(value)
  }

  get packageType() {
    return this._packageType
  }

  get installUrl() {
    return this._packageType === 'sandbox' ? this._installationUrlSandbox : this._installationUrl
  }

  get selectItems() {
    return AGENTFORCE_SELECT_ITEMS
  }
}
